import { FC, useCallback } from 'react';
import { useAtom } from 'jotai';
import {
  Avatar,
  Menu,
  Divider,
  MenuItem,
  ListItemAvatar,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  appBarMenuAnchorId,
  appBarMenuId,
} from 'packages/main/components/Layouts/PrivateLayout';
import {
  useAccessToken,
  useUserProfile,
} from 'packages/lib/utils/hooks/use-auth-data';
import { profileMenuOpenAtom } from 'packages/main/stores/profile-menu';
import { signOut } from 'packages/lib/auth';
import CopyToken from 'packages/lib/common-ui/copy-token';
import * as Sentry from '@sentry/react';

import ExitToAppIcon from '@mui/icons-material/ExitToApp';

const UserProfileItem: FC<React.PropsWithChildren<unknown>> = () => {
  const { userProfile } = useUserProfile();
  return (
    <>
      <MenuItem key="auth-menu-user-profile">
        <ListItemAvatar>
          <Avatar />
        </ListItemAvatar>
        {userProfile && (
          <ListItemText
            primary={userProfile.name}
            secondary={userProfile.email}
          />
        )}
      </MenuItem>
      <Divider key="auth-menu-profile-divider" />
    </>
  );
};

const DefaultMenuLayout: FC<React.PropsWithChildren<unknown>> = () => {
  const anchorEl = document.getElementById(appBarMenuAnchorId);
  const { accessToken } = useAccessToken();
  const [menuOpen, setMenuOpen] = useAtom(profileMenuOpenAtom);

  const handleMenuClose = useCallback((): void => {
    setMenuOpen(false);
  }, [setMenuOpen]);

  const handleSignOut = useCallback(
    async (): Promise<void> =>
      await signOut().catch((err: unknown) => {
        Sentry.captureException(err);
      }),
    []
  );

  return (
    <Menu
      anchorEl={menuOpen ? anchorEl : null}
      id={appBarMenuId}
      data-testid="profile-menu-popover"
      open={menuOpen}
      onClose={handleMenuClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      {!accessToken ? (
        <MenuItem>
          <div style={{ margin: '1rem 1.5rem' }}>
            Ah, the sweet smell of freshly cut drive logs...
            <i
              className="fa fa-spinner fa-spin"
              style={{ margin: '0.5rem' }}
            ></i>
          </div>
        </MenuItem>
      ) : (
        [
          <UserProfileItem key="user-profile-item-" />,
          <MenuItem key="auth-menu-copytext-button">
            <CopyToken copyText={accessToken} ui="list" />
          </MenuItem>,
          <Divider key="auth-menu-divider" />,
          <MenuItem key="auth-menu-signout-button" onClick={handleSignOut}>
            <ListItemIcon>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary="Sign Out" />
          </MenuItem>,
        ]
      )}
    </Menu>
  );
};

export default DefaultMenuLayout;
